// client/src/App.js
import React,  { useEffect } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation
} from 'react-router-dom';
import 'tailwindcss/tailwind.css';

import { isTokenValid } from './utils/auth';

import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import ConversationView from './pages/ConversationView';
import ProfilePage from './pages/ProfilePage';
import Settings from './pages/Settings';
import Layout from './components/Layout';
import AdminLayout from './pages/admin/AdminLayout';
import AdminOverview from './pages/admin/AdminOverview';
import AdminMessages from './pages/admin/AdminMessages';
import AdminConversations from './pages/admin/AdminConversations';
import AdminAgents from './pages/admin/AdminAgents';
import AdminAgentDetail from './pages/admin/AdminAgentDetail';
import AdminResponseTimes from './pages/admin/AdminResponseTimes';
import AdminTemplates from './pages/admin/AdminTemplates';
import AdminAudit from './pages/admin/AdminAudit';
import AdminFunnel from './pages/admin/AdminFunnel';
import AdminAlerts from './pages/admin/AdminAlerts';
import AdminBlocked from './pages/admin/AdminBlocked';
import AdminApiKeys from './pages/admin/AdminApiKeys';
import AdminUsers from './pages/admin/AdminUsers';

/**
 * Este componente vive “dentro” de <BrowserRouter>.
 * Aquí es donde podemos usar useLocation() para forzar la reevaluación
 * de isTokenValid() cada vez que cambie la ruta.
 */
function AppRoutes() {
  // useLocation() solo puede usarse dentro de un Router
  const location = useLocation();

  // Cada vez que cambie `location`, React vuelve a renderizar AppRoutes.
  const valid = isTokenValid();
  console.log('[AppRoutes] location:', location.pathname);

  return (
    <Routes>
      {/* 1) Raíz */}
      <Route
        path="/"
        element={
          valid
            ? <Navigate to="/dashboard" replace />
            : <Navigate to="/login" replace />
        }
      />

      {/* 2) Login */}
      <Route
        path="/login"
        element={
          valid
            ? <Navigate to="/dashboard" replace />
            : <Login />
        }
      />

      {/* 3) Register */}
      <Route
        path="/register"
        element={
          valid
            ? <Navigate to="/dashboard" replace />
            : <Register />
        }
      />

      {/* Rutas protegidas con Layout compartido */}
      {valid ? (
        <Route element={<Layout />}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/conversations/:id" element={<ConversationView />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminOverview />} />
            <Route path="messages" element={<AdminMessages />} />
            <Route path="conversations" element={<AdminConversations />} />
            <Route path="agents" element={<AdminAgents />} />
            <Route path="agents/:id" element={<AdminAgentDetail />} />
            <Route path="response-times" element={<AdminResponseTimes />} />
            <Route path="templates" element={<AdminTemplates />} />
            <Route path="audit" element={<AdminAudit />} />
            <Route path="funnel" element={<AdminFunnel />} />
            <Route path="alerts" element={<AdminAlerts />} />
            <Route path="blocked" element={<AdminBlocked />} />
            <Route path="api-keys" element={<AdminApiKeys />} />
            <Route path="users" element={<AdminUsers />} />
          </Route>
        </Route>
      ) : (
        <>
          <Route path="/dashboard" element={<Navigate to="/login" replace />} />
          <Route path="/conversations/:id" element={<Navigate to="/login" replace />} />
          <Route path="/profile" element={<Navigate to="/login" replace />} />
          <Route path="/settings" element={<Navigate to="/login" replace />} />
          <Route path="/admin/*" element={<Navigate to="/login" replace />} />
        </>
      )}

      {/* 8) Cualquier otra ruta reenlaza a "/" */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/**
 * En App solo montamos el <BrowserRouter> y dentro <AppRoutes>.
 * Así garantizamos que AppRoutes (y cualquier hook de react-router)
 * esté siempre dentro del contexto de enrutador.
 */
export default function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}
